import { Platform } from '@angular/cdk/platform';
import { ChangeDetectionStrategy, Component, ElementRef, inject, model, viewChild } from '@angular/core';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { TooltipDirective } from '../tooltip/tooltip.directive';
import { IconComponent } from '../icon/icon.component';
export type SearchScope = 'all' | 'filtered';

@Component({
	selector: 'skl-search-bar',
	template: `
		<div class="search-bar" (click)="searchInput.focus()">
			<skl-icon name="search-filled" size="20px" />
			<input
				#searchInput
				[value]="value()"
				(input)="onInput($event)"
				[placeholder]="placeholder()"
			/>
			@if (!isCompactView) {
				<span class="shortcut"><kbd>Ctrl</kbd>+<kbd>K</kbd></span>
			}
			<button
				class="clear-button"
				(click)="value.set(''); searchInput.focus()"
				[tooltip]="'ui.search-bar.clearTooltip' | translate"
			>
				<skl-icon name="close-circle-fill" size="20px" />
			</button>
		</div>
		<ng-content select="[slot='group-end']" />
	`,
	styles: `
		:host {
			--_focus-color: var(--primary-color-light);
			--_surface: var(--surface-3-color);
			--_border-color: light-dark(
				oklch(from black l c h / 12%),
				oklch(from white l c h / 4%)
			);

			display: flex;
			flex-wrap: wrap;
			align-items: stretch;
			container-type: inline-size;
			border-radius: var(--gutter-small);
			border: 1px solid var(--_border-color);
			background-color: var(--_surface);
			gap: var(--gutter-tiny);
			justify-content: space-between;
			transition: outline 0.2s ease, outline-offset 0.2s ease;

		}

		:host(:has(.search-bar input:focus)) {
			outline: 2px solid var(--_focus-color);
			outline-offset: 2px;
		}

		:host ::ng-deep [slot='group-end']:last-child {
			border-top-right-radius: calc(var(--gutter-small) - 1px);
			border-bottom-right-radius: calc(var(--gutter-small) - 1px);
		}

		@container (inline-size < 50ch) {
			.search-bar,
			:host ::ng-deep [slot='group-end'] {
				flex: 1 1 100%;
			}

			:host ::ng-deep [slot='group-end']:last-child {
				border-top-right-radius: initial;
				border-bottom-left-radius: calc(var(--gutter-small) - 1px);
				border-bottom-right-radius: calc(var(--gutter-small) - 1px);
			}
		}

		.search-bar {
			display: flex;
			flex: 1 1 auto;
			padding-inline-start: 0.75rem;
			gap: var(--gutter-small);
			height: 2.5rem;
			align-items: center;

			input {
				all: unset;
				font-size: 0.875rem;
				inline-size: 100%;
				max-inline-size: 100%;

				&:not(:placeholder-shown) {
					& ~ .shortcut {
						display: none;
					}

					& ~ .clear-button {
						display: grid;
					}
				}
			}

			skl-icon {
				color: var(--text-minor-color);
			}

			.shortcut {
				--_surface: var(--surface-4-color);

				font-size: 0.75rem;
				display: flex;
				user-select: none;
				justify-content: center;
				align-items: center;
				color: var(--text-minor-color);
				background-color: var(--_surface);
				border-radius: var(--gutter-small);
				box-shadow: 0 0 0 1px rgb(0 0 0 / 8%);
				height: 1.5rem;
				margin-inline-end: var(--gutter-small);

				@container (inline-size < 30ch) {
					display: none;
				}

				kbd {
					padding: var(--gutter-small);
					border-radius: var(--gutter-small);
				}
			}

			.clear-button {
				all: unset;
				display: none;
				place-content: center;
				color: var(--text-minor-color);
				background-color: transparent;
				height: 2rem;
				padding-inline: var(--gutter-small);
				border-radius: var(--gutter-small);
				cursor: pointer;
				transition: background-color 0.2s ease, outline 0.2s ease, outline-offset 0.2s ease;
				z-index: 1;

				&:hover {
					background-color: color-mix(in oklab, transparent, light-dark(black, white) 8%);
				}

				&:focus-visible {
					outline: 2px solid var(--_focus-color);
					outline-offset: -2px;
				}
			}
		}
	`,
	host: {
		'(document:keydown)': 'handleKeyboardEvent($event)'
	},
	imports: [TooltipDirective, TranslatePipe, IconComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchBarComponent {
	readonly #platform = inject(Platform);

	public readonly value = model<string>('');
	public readonly placeholder = model<string>('');

	protected get isCompactView(): boolean {
		return this.#platform.ANDROID || this.#platform.IOS;
	}

	protected readonly searchInput = viewChild<ElementRef<HTMLElement>>('searchInput');

	protected handleKeyboardEvent(event: KeyboardEvent) {
		if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
			event.preventDefault();

			const element = this.searchInput()?.nativeElement;
			element?.focus();
		}
	}

	protected onInput(event: Event) {
		const target = event.target as HTMLInputElement;
		this.value.set(target.value);
	}
}
